/**
 * Missed-AGENT_START adoption — shared by the text and thinking delta lanes
 * (extracted from streamingHandlers.ts; that file only ratchets down).
 *
 * 'idle' alone is ambiguous: it means "the turn ended" ONLY when THIS client
 * saw it end first-hand (turnSealedAt — stamped at AGENT_END, never persisted).
 * Without that knowledge — a reopened tab, a sync that truthfully said idle ms
 * before the next turn began — 'idle' + a live delta means a turn started
 * while we weren't listening (founder 08-24, prod task 8c5eee71: the dead
 * pane). Sealed recently → genuine stale replay, the caller drops. No seal
 * knowledge → adopt: open the turn the way the delayed-AGENT_START path
 * always has, and ask for a sync to backfill what the blind window ate.
 */
import { useTaskStore, requestTaskSync } from "../taskStore";
import { SEALED_TURN_TRUST_MS } from "./syncHandlers";

/** First-hand "I saw this turn end" knowledge, still fresh. */
export function sealedRecentlyFor(task: {
  turnSealedAt?: number | null;
}): boolean {
  return (
    task.turnSealedAt != null &&
    Date.now() - task.turnSealedAt < SEALED_TURN_TRUST_MS
  );
}

/**
 * Open the missed turn: overlay (seeded with the first text delta when the
 * text lane calls), phase, clocks — then a deduped backfill sync (1.5s
 * dedupe — never floods). No-op if an overlay already exists.
 */
export function adoptMissedTurn(
  taskId: string,
  agentSlug: string,
  firstText?: string
): void {
  useTaskStore.getState()._mutateTasks((tasks) => {
    const t = tasks.get(taskId);
    if (!t || t.streamingMessage) return;
    t.phase = "processing";
    const clientId = `msg-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 7)}`;
    t.streamingMessage = {
      id: clientId,
      role: "assistant",
      content:
        firstText !== undefined
          ? [{ type: "text" as const, text: firstText }]
          : [],
      timestamp: new Date(),
      agentSlug,
      identity: { kind: "optimistic", clientId },
    };
    t.turnStartTime = Date.now();
    t.agentStartedAt = Date.now();
  });
  requestTaskSync(taskId);
}
